import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  createDataProcessingConsent,
  getActiveDataProcessingConsent,
  getDataProcessingConsents,
} from '@app/api/data-processing-consent.api'

import { RodoDocumentsStatus, Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'
import { ActiveDocument, CreateDocumentsPayload, RodoDocumentsResponse } from '@/api/rodo-policies.api'

type DataProcessingConsentsState = {
  items: RodoDocumentsResponse[]
  activeDocument: ActiveDocument
  status: Status
  createStatus: Status
  activeDocumentStatus: Status
  error: string | null
}

const initialState: DataProcessingConsentsState = {
  items: [],
  activeDocument: null,
  status: 'idle',
  createStatus: 'idle',
  activeDocumentStatus: 'idle',
  error: null,
}

export const getDataProcessingConsentsData = createAsyncThunk<RodoDocumentsResponse[]>(
  'dataProcessingConsents/all',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getDataProcessingConsents()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getActiveDataProcessingConsentData = createAsyncThunk<ActiveDocument>(
  'dataProcessingConsents/active',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getActiveDataProcessingConsent()
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createDataProcessingConsentDocument = createAsyncThunk<string, CreateDocumentsPayload>(
  'dataProcessingConsents/create',
  async (createDataProcessingConsentPayload, { rejectWithValue }) => {
    try {
      const id = await createDataProcessingConsent(createDataProcessingConsentPayload)

      return id
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const dataProcessingConsentsSlice = createSlice({
  name: 'dataProcessingConsents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDataProcessingConsentsData.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(createDataProcessingConsentDocument.pending, (state) => {
        state.createStatus = 'loading'
      })
      .addCase(getActiveDataProcessingConsentData.pending, (state) => {
        state.activeDocumentStatus = 'loading'
      })
      .addCase(getDataProcessingConsentsData.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(createDataProcessingConsentDocument.rejected, (state) => {
        state.createStatus = 'failed'
      })
      .addCase(getActiveDataProcessingConsentData.rejected, (state) => {
        state.activeDocumentStatus = 'failed'
      })
      .addCase(getDataProcessingConsentsData.fulfilled, (state, action) => {
        state.items = action.payload
        state.status = 'succeeded'
      })
      .addCase(getActiveDataProcessingConsentData.fulfilled, (state, action) => {
        state.activeDocument = action.payload
        state.activeDocumentStatus = 'succeeded'
      })
      .addCase(createDataProcessingConsentDocument.fulfilled, (state, action) => {
        const id = action.payload
        const requestData = action.meta.arg
        const activeIndex = state.items.findIndex((it) => it.status === 'ACTIVE')
        const updatedItems = [...state.items]

        if (activeIndex !== -1) {
          const active = updatedItems[activeIndex]
          updatedItems[activeIndex] = { ...active, status: 'ARCHIVED' as RodoDocumentsStatus }
        }

        const newItem = {
          id,
          status: 'ACTIVE' as RodoDocumentsStatus,
          createdAt: new Date().toISOString(),
          ...requestData,
        }

        state.items = [newItem, ...updatedItems]

        state.createStatus = 'succeeded'
      })
  },
})

export default dataProcessingConsentsSlice.reducer
